import React, { useState, useRef, useEffect } from "react";
import LoadingBar from "react-top-loading-bar";
import TopBar from "../../sections/TopBar";
import Sidebar from "../../sections/Sidebar";
import "../../customCss/ManageOfficers.css";
import DataService from "../../services/data.service";
import { toast } from "react-toastify";
import { Modal } from "react-bootstrap";
import NotFound from "../../images/no-data-found.jpeg";
import Spinner from "../../sections/Spinner";
import {
    TextField,
    Autocomplete,
    Grid,
    Box,
    Button
} from "@mui/material";
import IndustryCrmForm, { industryCategories } from "./IndustryCrmForm";
import dayjs from "dayjs";

const IndustryCrmList = () => {
    const ref = useRef();
    const [isSidebarOpen, setIsSidebarOpen] = useState(true);
    const [allContacts, setAllContacts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [openForm, setOpenForm] = useState(false);
    const [selectedContact, setSelectedContact] = useState(null);
    const [deletePopup, setDeletePopup] = useState(false);
    const [btnLoading, setBtnLoading] = useState(false);
    const [viewMode, setViewMode] = useState(false);
    const [filterData, setFilterData] = useState({
        industryCategory: null,
        subCategory: null,
        city: ""
    });

    useEffect(() => {
        document.title = "Industry CRM || Admin Panel";
        window.scrollTo(0, 0);
        getAllContacts();

        const handleResize = () => {
            if (window.innerWidth < 768) setIsSidebarOpen(false);
        };
        window.addEventListener("resize", handleResize);
        handleResize();
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const toggleSidebar = () => setIsSidebarOpen((prev) => !prev);

    const getAllContacts = (params = {}) => {
        ref.current.continuousStart();
        setLoading(true);
        DataService.getAllContacts(params)
            .then((res) => {
                setAllContacts(res?.data?.data || []);
                ref.current.complete();
                setLoading(false);
            })
            .catch((err) => {
                console.error(err);
                toast.error("Failed to fetch contacts");
                ref.current.complete();
                setLoading(false);
            });
    };

    const handleAdd = () => {
        setSelectedContact(null);
        setViewMode(false);
        setOpenForm(true);
    };

    const handleEdit = (contact) => {
        setSelectedContact(contact);
        setViewMode(false);
        setOpenForm(true);
    };

    const handleView = (contact) => {
        setSelectedContact(contact);
        setViewMode(true);
        setOpenForm(true);
    };

    const handleDelete = () => {
        if (!selectedContact) return;
        setBtnLoading(true);
        DataService.deleteContact(selectedContact._id)
            .then(() => {
                toast.success("Record deleted successfully");
                setDeletePopup(false);
                getAllContacts();
            })
            .catch((err) => {
                const msg = err.response?.data?.message || "Failed to delete record";
                toast.error(msg);
            })
            .finally(() => {
                setBtnLoading(false);
            });
    };

    const handleSearch = () => {
        const params = {};
        if (filterData.industryCategory) params.industryCategory = filterData.industryCategory;
        if (filterData.subCategory) params.subCategory = filterData.subCategory;
        if (filterData.city) params.city = filterData.city;
        getAllContacts(params);
    };

    const handleClear = () => {
        setFilterData({
            industryCategory: null,
            subCategory: null,
            city: ""
        });
        getAllContacts();
    };

    const filteredSubCategories = filterData.industryCategory
        ? industryCategories[filterData.industryCategory] || []
        : [];

    const handleFormSubmit = (formData) => {
        setBtnLoading(true);
        if (selectedContact) {
            DataService.updateContact(selectedContact._id, formData)
                .then(() => {
                    toast.success("Record updated successfully");
                    setOpenForm(false);
                    getAllContacts();
                })
                .catch((err) => {
                    const msg = err.response?.data?.message || "Failed to update record";
                    toast.error(msg);
                })
                .finally(() => setBtnLoading(false));
        } else {
            DataService.addContact(formData)
                .then(() => {
                    toast.success("Record added successfully");
                    setOpenForm(false);
                    getAllContacts();
                })
                .catch((err) => {
                    const msg = err.response?.data?.message || "Failed to add record";
                    toast.error(msg);
                })
                .finally(() => setBtnLoading(false));
        }
    };

    return (
        <>
            <LoadingBar color="#084e8a" ref={ref} height={5} />
            <div className={`main_layout ${isSidebarOpen ? "sidebar-open" : "sidebar-closed"}`}>
                <Sidebar isSidebarOpen={isSidebarOpen} />
                <div className="innerRight">
                    <TopBar title="Industry CRM" menu="" toggleSidebar={toggleSidebar} />
                    <div className="officersSec">
                        <div className="row align-items-center mb-3">
                            <div className="col-sm-6 col-6">
                                <h3 className="colorHeadThree mb-0">Industry CRM Records</h3>
                            </div>
                            <div className="col-sm-6 col-6 text-right">
                                <button className="btn btn-primary" onClick={handleAdd}>
                                    <i className="fas fa-plus-circle me-2" />
                                    Add New
                                </button>
                            </div>
                        </div>

                        <Box sx={{ mb: 3, p: 2, bgcolor: "#f8f9fa", borderRadius: "8px" }}>
                            <Grid container spacing={2} alignItems="center">
                                <Grid item xs={12} sm={3}>
                                    <Autocomplete
                                        options={Object.keys(industryCategories)}
                                        value={filterData.industryCategory}
                                        onChange={(event, newValue) => {
                                            setFilterData(prev => ({ ...prev, industryCategory: newValue, subCategory: null }));
                                        }}
                                        sx={{ background: "#fff" }}
                                        renderInput={(params) => (
                                            <TextField {...params} label="Category" size="small" fullWidth />
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={3}>
                                    <Autocomplete
                                        options={filteredSubCategories}
                                        value={filterData.subCategory}
                                        onChange={(event, newValue) => {
                                            setFilterData(prev => ({ ...prev, subCategory: newValue }));
                                        }}
                                        sx={{ background: "#fff" }}
                                        disabled={!filterData.industryCategory}
                                        renderInput={(params) => (
                                            <TextField {...params} label="Sub-category" size="small" fullWidth />
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={3}>
                                    <TextField
                                        fullWidth
                                        label="City"
                                        size="small"
                                        sx={{ background: "#fff" }}
                                        value={filterData.city}
                                        onChange={(e) => setFilterData(prev => ({ ...prev, city: e.target.value }))}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={3}>
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                        <Button variant="contained" onClick={handleSearch} fullWidth>
                                            Search
                                        </Button>
                                        <Button variant="outlined" onClick={handleClear} fullWidth>
                                            Clear
                                        </Button>
                                    </Box>
                                </Grid>
                            </Grid>
                        </Box>

                        {allContacts.length > 0 ? (
                            <div className="tableScroller">
                                <table className="table table-striped">
                                    <thead style={{ whiteSpace: "nowrap" }}>
                                        <tr>

                                            <th scope="col">Company Name</th>
                                            <th scope="col">Industry</th>
                                            <th scope="col">City</th>
                                            <th scope="col">Contact Name</th>
                                            <th scope="col">Phone</th>
                                            <th scope="col">Email</th>
                                            <th scope="col">Status</th>
                                            <th scope="col" className="text-center">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody style={{ whiteSpace: "nowrap" }}>
                                        {allContacts.map((item, i) => (
                                            <tr key={i}>
                                                <td>{item.companyName || "-"}</td>
                                                <td>{item.industryCategory || "-"}</td>
                                                <td>{item.city || "-"}</td>
                                                <td>{item.contactName || "-"}</td>
                                                <td>{item.phoneNumber || "-"}</td>
                                                <td>{item.email || "-"}</td>
                                                <td>
                                                    <span className={`badge ${item.status === 'active' ? 'bg-success' :
                                                        item.status === 'closed' ? 'bg-secondary' :
                                                            item.status === 'lost' ? 'bg-danger' : 'bg-warning'
                                                        } text-white`}>
                                                        {item.status || "Pending"}
                                                    </span>
                                                </td>
                                                <td className="text-center">
                                                    <div className="tIco">
                                                        <i
                                                            className="fas fa-eye me-3"
                                                            onClick={() => handleView(item)}
                                                            title="View"
                                                            style={{ cursor: 'pointer' }}
                                                        />
                                                        <i
                                                            className="fas fa-pencil-alt me-3"
                                                            onClick={() => handleEdit(item)}
                                                            title="Edit"
                                                            style={{ cursor: 'pointer' }}
                                                        />
                                                        <i
                                                            className="fas fa-trash-alt me-3"
                                                            onClick={() => {
                                                                setSelectedContact(item);
                                                                setDeletePopup(true);
                                                            }}
                                                            title="Delete"
                                                            style={{ cursor: 'pointer' }}
                                                        />
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            !loading && (
                                <div className="text-center mt-4">
                                    <img src={NotFound} alt="noData" className="noData mb-2" />
                                    <p className="m-0">No Records Found.</p>
                                </div>
                            )
                        )}
                    </div>
                </div>
            </div>

            <IndustryCrmForm
                open={openForm}
                onClose={() => setOpenForm(false)}
                initialData={selectedContact}
                onSubmit={handleFormSubmit}
                loading={btnLoading}
                readOnly={viewMode}
            />

            <Modal
                show={deletePopup}
                onHide={() => setDeletePopup(false)}
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>Delete Record</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="text-center">
                        <p className="mb-0">
                            Are you sure you want to delete the record for <strong>{selectedContact?.companyName}</strong>?
                        </p>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <button
                        type="button"
                        className="btn btn-primary"
                        onClick={handleDelete}
                        disabled={btnLoading}
                    >
                        {btnLoading ? <Spinner /> : "Delete"}
                    </button>
                    <button
                        type="button"
                        className="btn btn-danger"
                        onClick={() => setDeletePopup(false)}
                    >
                        Cancel
                    </button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default IndustryCrmList;
